import { useEffect, useState } from 'react';
import {
  FIELD_SUGGESTIONS_MIN_CHARS,
  useFieldSuggestionsQuery,
} from '../../services/field-suggestions/fieldSuggestionsQueries';

/** @type {number} */
const SUGGESTIONS_DEBOUNCE_MS = 250;

/**
 * Debounced field suggestions for form text autocomplete.
 * @param {object} params
 * @param {string | null | undefined} params.suggestionsUrl
 * @param {string} params.value - Current input value
 * @param {boolean} [params.enabled=true] - When false, skip fetching (e.g. after selecting a suggestion)
 * @returns {{
 *   suggestions: string[],
 *   isPending: boolean,
 * }}
 */
function useFieldSuggestions({ suggestionsUrl, value, enabled = true }) {
  const [debouncedValue, setDebouncedValue] = useState(
    typeof value === 'string' ? value : '',
  );

  useEffect(() => {
    const nextValue = typeof value === 'string' ? value : '';
    const timeoutId = setTimeout(() => {
      setDebouncedValue(nextValue);
    }, SUGGESTIONS_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value]);

  const search = enabled && debouncedValue.length >= FIELD_SUGGESTIONS_MIN_CHARS
    ? debouncedValue
    : '';

  const { data, isPending, isFetching } = useFieldSuggestionsQuery(
    suggestionsUrl,
    search,
  );

  return {
    suggestions: search ? (data ?? []) : [],
    isPending: Boolean(search) && (isPending || isFetching),
  };
}

export default useFieldSuggestions;
